import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { Router } from '@angular/router';
import { NavController } from '@ionic/angular';

export interface PresentationState {
  mode: 'default' | 'fullscreen' | 'modal';
  options?: {
    hideMainHeader: boolean;
    showCloseButton: boolean;
    closeAction: 'back' | 'navigate' | 'home';
    closeTarget?: string;
    headerTitle?: string;
    headerSubtitle?: string;
  };
}

@Injectable({
  providedIn: 'root'
})
export class PresentationService {
  private presentationState = new BehaviorSubject<PresentationState>({
    mode: 'default'
  });

  public readonly presentationState$: Observable<PresentationState> = 
    this.presentationState.asObservable();

  constructor(
    private router: Router,
    private navCtrl: NavController
  ) {}

  /**
   * Set the presentation mode for the current page
   */
  setPresentation(mode: 'default' | 'fullscreen' | 'modal', options?: PresentationState['options']): void {
    this.presentationState.next({ mode, options });
  }

  /**
   * Get the current presentation state
   */
  getCurrentState(): PresentationState {
    return this.presentationState.value;
  }

  /**
   * Check if currently in fullscreen mode
   */
  isFullscreenMode(): boolean {
    return this.presentationState.value.mode === 'fullscreen';
  }

  /**
   * Handle close action based on configuration
   */
  async handleClose(): Promise<void> {
    const state = this.presentationState.value;
    
    if (!state.options) {
      this.navCtrl.back();
      return;
    }

    switch (state.options.closeAction) {
      case 'back':
        this.navCtrl.back();
        break;
      
      case 'navigate':
        if (state.options.closeTarget) {
          // Reset to default mode before navigating
          this.setPresentation('default');
          await this.router.navigate([state.options.closeTarget]);
        } else {
          this.navCtrl.back();
        }
        break;
      
      case 'home':
        // Reset to default mode before navigating home
        this.setPresentation('default');
        await this.router.navigate(['/']);
        break;
      
      default:
        this.navCtrl.back();
    }
  }

  /**
   * Reset to default presentation mode
   */
  reset(): void {
    this.setPresentation('default');
  }
}